// Controlled subscription-detail dialog — the ONE dialog shown wherever a subscription is opened (the Recurring
// report AND the per-PSP Subscriptions tab). Shows a subscription's status/amount/next payment/installments and
// its payments, each classified AT RUNTIME by /api/manage/pledge-subscription (no stored match pointer):
//   matched   — an order currently carries this payment's id (exact, or ⚠ within ±2d/±5% tolerance)
//   needs_link — a unique in-tolerance order exists → click to review the tx + order and link them
//   unbooked  — no order within tolerance
// One dialog, one code path, every surface — no duplication.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Alert, Tooltip, Snackbar,
  Autocomplete, TextField, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { get, post } from '../../utils/api';
import { customers } from '../../services/kioskApi';

const custName = (c) => (c ? `${c.first_name || c.billing_first_name || ''} ${c.last_name || c.billing_last_name || ''}`.trim() || c.name || `#${c.id || c.customer_id}` : '');

const PROVIDER_LABEL = { paysimple: 'PaySimple', authnet: 'Authorize.Net' };
const fmtMoney = (n) => (n == null || n === '' ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const statusColor = (s) => (s === 'matched' ? 'success' : s === 'needs_link' ? 'info' : s === 'unbooked' ? 'error' : 'warning');
const statusLabel = (s) => (s === 'matched' ? 'Linked' : s === 'unbooked' ? 'No order' : s === 'needs_link' ? 'Needs link' : s); // order wording

function Field({ label, value, mono }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 500, ...(mono ? { fontFamily: 'monospace' } : {}) }}>{value}</Typography>
    </Box>
  );
}

const toleranceNote = (c) => {
  const parts = [];
  if (c.day_diff != null && c.day_diff > 0) parts.push(`${c.day_diff} day${c.day_diff === 1 ? '' : 's'} apart`);
  if (c.amount_diff != null && c.amount_diff >= 0.005) parts.push(`amount ${fmtMoney(c.amount_diff)} off`);
  return parts.length ? `Matched within tolerance — ${parts.join(' · ')}` : 'Matched within tolerance';
};

// Nested confirm dialog: shows the PSP transaction and the candidate LDB order side by side, then links them.
function LinkChargeDialog({ open, onClose, provider, account, charge, onLinked }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  if (!charge) return null;
  const within = charge.within_tolerance;
  const orders = (charge.orders && charge.orders.length) ? charge.orders : (charge.order_id ? [{ order_id: charge.order_id, order_number: charge.order_number, order_date: charge.order_date, order_amount: charge.order_amount, product_label: charge.product_label }] : []);
  const submit = async () => {
    setSaving(true); setErr('');
    try {
      for (const o of orders) {
        if (provider === 'paysimple') await post('/api/manage/paysimple-reconcile/link', { payment_id: charge.payment_id, order_id: o.order_id });
        else await post('/api/manage/authnet-reconcile/link', { trans_id: charge.payment_id, account, order_id: o.order_id });
      }
      onLinked();
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Link failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>Link payment to order{orders.length > 1 ? 's' : ''}<IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>{PROVIDER_LABEL[provider] || provider} transaction</Typography>
            <Field label="Date" value={fmtDate(charge.date)} />
            <Field label="Amount" value={fmtMoney(charge.amount)} />
            <Field label="Tx ID" value={charge.payment_id} mono />
            {charge.method && <Field label="Method" value={charge.method} />}
          </Box>
          <Box sx={{ border: 1, borderColor: within ? 'success.light' : 'warning.light', borderRadius: 1, p: 1.5, bgcolor: within ? 'success.50' : 'warning.50' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>LDB order{orders.length > 1 ? `s (${orders.length})` : ''}</Typography>
            {orders.length ? (<>
              {orders.map((o, k) => (
                <Box key={k} sx={{ mb: k < orders.length - 1 ? 1 : 0, pb: k < orders.length - 1 ? 1 : 0, borderBottom: k < orders.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                  <Field label="Order" value={`#${o.order_number}`} mono />
                  <Field label="Date" value={fmtDate(o.order_date)} />
                  <Field label="Amount" value={fmtMoney(o.order_amount)} />
                  {o.product_label && <Field label="Product / variation" value={o.product_label} />}
                </Box>
              ))}
              <Box sx={{ mt: 1 }}>
                {within ? <Chip size="small" color="success" label="Within tolerance" />
                  : <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={toleranceNote(charge)} />}
              </Box>
            </>) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No order within tolerance — nothing to link (needs an order created).</Typography>}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="primary" startIcon={<LinkIcon />} disabled={saving || !orders.length} onClick={submit}>{saving ? 'Linking…' : (orders.length > 1 ? `Link all ${orders.length}` : 'Link them')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// A who-is-this box (payer vs the portal devotee the order books to).
function IdentityCard({ title, name, email, tone, children }) {
  return (
    <Box sx={{ p: 1.5, border: 2, borderColor: tone === 'accent' ? 'primary.main' : 'divider', borderRadius: 1.5, height: '100%', bgcolor: tone === 'accent' ? 'action.hover' : 'background.paper' }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>{title}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>{name || '—'}</Typography>
      {email ? <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{email}</Typography> : null}
      {children}
    </Box>
  );
}

// Create + link an order for a no-order payment. The order books to a DEVOTEE (pre-filled from the mapping when
// the subscription is mapped, otherwise chosen here) and an ALLOCATION (pre-filled from the mapping, fully
// editable; for an unmapped subscription you pick the product(s) from scratch). Submitting creates the order
// back-dated + stamped with the tx id, so it reconciles immediately — no trip to the order screen.
function CreateOrderDialog({ open, onClose, provider, account, charge, mapping, donorName, onCreated }) {
  const [splits, setSplits] = useState([]);
  const [opts, setOpts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null); // chosen devotee { id, name, email }
  const [changing, setChanging] = useState(false);
  const [devOpts, setDevOpts] = useState([]);
  const [preview, setPreview] = useState(null); // authnet payer/suggestions/dup (best-effort)
  const [dup, setDup] = useState(null);

  // Seed splits + devotee from the mapping (if any) whenever opened.
  useEffect(() => {
    if (!open) return;
    const ds = mapping && Array.isArray(mapping.splits) ? mapping.splits : [];
    setSplits(ds.length
      ? ds.map((s) => ({ target: { product_id: Number(s.product_id), variation_id: Number(s.variation_id) || 0, label: s.label || 'Allocation' }, amount: String(s.amount ?? '') }))
      : [{ target: null, amount: charge ? String(charge.amount) : '' }]);
    setSelected(mapping && mapping.customer_id ? { id: Number(mapping.customer_id), name: mapping.customer_name || '', email: mapping.customer_email || null } : null);
    setChanging(!(mapping && mapping.customer_id)); // open the picker straight away when unmapped
    setErr(''); setDup(null); setPreview(null);
  }, [open, mapping, charge]);

  // AuthNet only: pull payer identity + ranked devotee suggestions + an initial duplicate check (best-effort).
  useEffect(() => {
    if (!open || provider !== 'authnet' || !charge) return undefined;
    let cancel = false;
    (async () => {
      try { const p = await get('/api/manage/authnet-reconcile/create-preview', { account, trans_id: charge.payment_id }); if (!cancel) { setPreview(p); setDup(p?.duplicate || null); } }
      catch { if (!cancel) setPreview(null); }
    })();
    return () => { cancel = true; };
  }, [open, provider, account, charge]);

  // Re-check duplicates for the chosen devotee (AuthNet has the endpoint; PaySimple skips).
  useEffect(() => {
    if (!open || provider !== 'authnet' || !charge || !selected?.id) return undefined;
    let cancel = false;
    (async () => {
      try { const r = await get('/api/manage/authnet-reconcile/dup-check', { customer_id: selected.id, amount: charge.amount, date: String(charge.date).slice(0, 10), exclude_trans_id: charge.payment_id }); if (!cancel) setDup(r?.duplicate || null); }
      catch { if (!cancel) setDup(null); }
    })();
    return () => { cancel = true; };
  }, [open, provider, charge, selected?.id]);

  const searchBase = provider === 'authnet' ? '/api/manage/authnet/product-search' : '/api/manage/paysimple/product-search';
  const search = async (term) => { if (!term || term.length < 2) { setOpts([]); return; } try { const r = await get(searchBase, { q: term }); setOpts(Array.isArray(r?.products) ? r.products : []); } catch { setOpts([]); } };
  const searchDev = async (q) => { if (!q || q.length < 2) { setDevOpts([]); return; } try { const list = await customers.searchUsers(q); setDevOpts(Array.isArray(list) ? list.slice(0, 10) : []); } catch { setDevOpts([]); } };
  const setLine = (i, k, v) => setSplits((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const sum = Math.round(splits.reduce((a, s) => a + (parseFloat(s.amount) || 0), 0) * 100) / 100;
  const fee = charge ? Math.round((charge.amount - sum) * 100) / 100 : 0;
  const payerName = (preview?.anet?.name) || donorName || '';
  const changed = selected && mapping && mapping.customer_id && Number(selected.id) !== Number(mapping.customer_id);
  const nameMismatch = selected && payerName && custName({ name: selected.name }).toLowerCase() !== payerName.toLowerCase();
  const valid = charge && selected?.id && splits.length && splits.every((s) => s.target && (parseFloat(s.amount) || 0) > 0) && sum > 0 && sum <= charge.amount + 0.005;
  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const clean = splits.filter((s) => s.target && (parseFloat(s.amount) || 0) > 0).map((s) => ({ product_id: s.target.product_id, variation_id: s.target.variation_id || 0, label: s.target.label, amount: parseFloat(s.amount) }));
      const url = provider === 'authnet' ? '/api/manage/authnet-reconcile/create-order' : '/api/manage/paysimple-reconcile/create-order';
      const body = provider === 'authnet'
        ? { trans_id: charge.payment_id, account, splits: clean, customer_id: selected.id }
        : { payment_id: charge.payment_id, splits: clean, customer_id: selected.id };
      await post(url, body);
      onCreated();
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Create failed'); setSaving(false); }
  };
  if (!charge) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>Create order for this payment<IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
          <Field label="Date" value={fmtDate(charge.date)} />
          <Field label="Charge" value={fmtMoney(charge.amount)} />
          <Field label="Tx ID" value={charge.payment_id} mono />
        </Box>

        {/* WHO: payer vs the portal devotee the order books to */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1 }}>
          <IdentityCard title={`${PROVIDER_LABEL[provider] || provider} (payer)`} name={payerName} email={preview?.anet?.email} />
          <IdentityCard title={`Portal devotee · order books to${changed ? ' (changed)' : ''}`} name={selected ? custName({ name: selected.name }) : 'choose a devotee'} email={selected?.email} tone="accent">
            <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => setChanging((v) => !v)} sx={{ mt: 0.5, px: 0.5 }}>{changing ? 'Keep' : 'Change devotee'}</Button>
          </IdentityCard>
        </Box>
        {!selected && <Alert severity="info" sx={{ mb: 1 }}>This subscription isn’t mapped to a devotee — choose who the order books to.</Alert>}
        {nameMismatch && <Alert severity="info" sx={{ mb: 1 }}>Payer name (<b>{payerName}</b>) differs from the chosen devotee (<b>{custName({ name: selected.name })}</b>). Can be fine (family/spouse) — just verify.</Alert>}
        {changing && (
          <Box sx={{ mb: 1.5 }}>
            <Autocomplete options={devOpts} filterOptions={(x) => x}
              getOptionLabel={(o) => `${custName(o)}${o.email ? ` · ${o.email}` : ''}`}
              onInputChange={(e, v) => searchDev(v)}
              onChange={(e, v) => { if (v) { setSelected({ id: v.id, name: custName(v), email: v.email || v.billing_email || null }); setChanging(false); } }}
              renderInput={(params) => <TextField {...params} size="small" label="Search devotee by name or email" autoFocus />} />
            {Array.isArray(preview?.suggestions) && preview.suggestions.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Suggested:</Typography>
                {preview.suggestions.slice(0, 4).map((s) => (
                  <Chip key={s.customer_id} size="small" label={`${s.name}${s.confidence === 'high' ? ' ✓' : ''}`} onClick={() => { setSelected({ id: s.customer_id, name: s.name, email: s.email }); setChanging(false); }} />
                ))}
              </Box>
            )}
          </Box>
        )}
        {dup && <Alert severity="warning" sx={{ mb: 1 }}>A matching order already exists for this devotee: <b>#{dup.order_number || dup.id}</b> on {fmtDate(dup.dt)} for {fmtMoney(dup.total_num)}. Creating may be a <b>duplicate</b> — consider linking instead.</Alert>}

        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Allocation{mapping && mapping.customer_id ? ' — pre-filled from the mapping; change as needed' : ' — choose the product(s) for this charge'}</Typography>
        {splits.map((s, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Autocomplete sx={{ flex: 1 }} size="small" options={opts} value={s.target}
              isOptionEqualToValue={(o, v) => o.product_id === v.product_id && (o.variation_id || 0) === (v.variation_id || 0)}
              getOptionLabel={(o) => o?.label || ''}
              onChange={(e, v) => setLine(i, 'target', v)}
              onInputChange={(e, v, reason) => { if (reason === 'input') search(v); }}
              renderOption={(props, o) => <li {...props} key={`${o.product_id}:${o.variation_id || 0}`}>{o.label}</li>}
              renderInput={(params) => <TextField {...params} placeholder="Search product / variation…" />} />
            <TextField size="small" type="number" value={s.amount} onChange={(e) => setLine(i, 'amount', e.target.value)} sx={{ width: 110 }} InputProps={{ startAdornment: '$' }} />
            {splits.length > 1 && <IconButton size="small" onClick={() => setSplits((p) => p.filter((_, idx) => idx !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>}
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={() => setSplits((p) => [...p, { target: null, amount: '' }])}>Add line</Button>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
          <Typography variant="body2">Allocation: <b>{fmtMoney(sum)}</b></Typography>
          <Typography variant="body2" sx={{ color: fee < -0.005 ? 'error.main' : 'text.secondary' }}>Fee: {fmtMoney(fee)}</Typography>
        </Box>
        {fee < -0.005 && <Alert severity="warning" sx={{ mt: 1 }}>Allocation exceeds the charge — reduce it.</Alert>}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>Books back-dated to {fmtDate(charge.date)}, no email sent.{changed ? ' Devotee changed from the mapping for this order only.' : ''}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" startIcon={<AddCircleOutlineIcon />} disabled={!valid || saving} onClick={submit}>{saving ? 'Creating…' : (dup ? 'Create anyway & link' : 'Create & link')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SubscriptionDetailDialog({ open, onClose, provider, subId, account, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [linkCharge, setLinkCharge] = useState(null);
  const [createCharge, setCreateCharge] = useState(null);
  const [toast, setToast] = useState('');
  const [active, setActive] = useState(null); // Set of shown statuses, or null = all

  const load = useCallback(async () => {
    if (!open || !provider || !subId) return;
    setLoading(true); setErr(''); setData(null); setActive(null);
    try { setData(await get('/api/manage/pledge-subscription', { provider, sub_id: subId, account: account || '' })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load subscription'); }
    finally { setLoading(false); }
  }, [open, provider, subId, account]);
  useEffect(() => { load(); }, [load]);

  const onLinked = async () => { setLinkCharge(null); setToast('Payment linked'); await load(); if (onChanged) onChanged(); };
  const onCreated = async () => { setCreateCharge(null); setToast('Order created & linked'); await load(); if (onChanged) onChanged(); };

  const inst = data && data.total_occurrences != null
    ? `${data.past_occurrences ?? 0} of ${data.total_occurrences}`
    : (data && data.past_occurrences != null ? `${data.past_occurrences} paid` : '—');
  const charges = data?.charges || [];
  const unbooked = charges.filter((c) => c.status === 'unbooked').length;
  // Status filter: one toggle chip per status present (counts shown). Default all on; "Needs attention" drops the
  // Linked rows (usually the bulk, and not actionable) so staff can focus on needs-link / no-order ones.
  const counts = {}; charges.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
  const statusRank = { unbooked: 0, needs_link: 1, matched: 3 };
  const statuses = Object.keys(counts).sort((a, b) => (statusRank[a] ?? 2) - (statusRank[b] ?? 2) || a.localeCompare(b));
  const isOn = (s) => !active || active.has(s);
  const toggle = (s) => setActive((prev) => { const next = new Set(prev || statuses); if (next.has(s)) next.delete(s); else next.add(s); return next; });
  const shown = charges.filter((c) => isOn(c.status));

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Recurring subscription
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          : err ? <Alert severity="error">{err}</Alert>
            : data ? (
              <>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'primary.50', borderColor: 'primary.100' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)' }, gap: 2 }}>
                    <Field label="Provider" value={PROVIDER_LABEL[data.provider] || data.provider} />
                    <Field label="Subscription" value={`${data.sub_id}${data.account ? ` · ${data.account}` : ''}`} mono />
                    <Field label="Status" value={<Chip size="small" color={/active|progress/i.test(String(data.status || '')) ? 'success' : 'default'} label={data.status || '—'} />} />
                    <Field label="Amount" value={`${fmtMoney(data.amount)}/mo`} />
                    <Field label="Installments" value={inst} />
                    <Field label="Next payment" value={fmtDate(data.next_payment_date)} />
                  </Box>
                  {data.donor_name && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>Donor: <b>{data.donor_name}</b></Typography>}
                  {data.name && <Typography variant="body2">{data.name}</Typography>}
                  {data.mapping && Array.isArray(data.mapping.splits) && data.mapping.splits.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Mapped allocation{data.mapping.customer_name ? ` · ${data.mapping.customer_name}` : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                        {data.mapping.splits.map((s, i) => (
                          <Chip key={i} size="small" variant="outlined" color="primary" label={`${s.label} · ${fmtMoney(s.amount)}`} sx={{ bgcolor: 'background.paper' }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                  Payments posted{unbooked > 0 ? ` · ${unbooked} without an order` : ''}
                </Typography>
                {statuses.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Show:</Typography>
                    {statuses.map((s) => (
                      <Chip key={s} size="small" clickable onClick={() => toggle(s)}
                        color={statusColor(s)} variant={isOn(s) ? 'filled' : 'outlined'}
                        label={`${statusLabel(s)} ${counts[s]}`}
                        sx={{ opacity: isOn(s) ? 1 : 0.55, fontWeight: 600 }} />
                    ))}
                    {counts.matched > 0 && statuses.some((s) => s !== 'matched') && (
                      <Button size="small" onClick={() => setActive(new Set(statuses.filter((s) => s !== 'matched')))}>Needs attention</Button>
                    )}
                    {active && <Button size="small" onClick={() => setActive(null)}>Show all</Button>}
                  </Box>
                )}
                {charges.length ? (
                  <Table component={Paper} variant="outlined" size="small" sx={{ borderRadius: 2, '& td, & th': { px: 1.25 }, '& thead th': { bgcolor: 'grey.50', fontWeight: 700 } }}>
                    <TableHead><TableRow>
                      <TableCell>Tx ID</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Method</TableCell>
                      <TableCell>Status</TableCell><TableCell>Order</TableCell><TableCell>Order date</TableCell><TableCell>Product / variation</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {shown.length === 0 && (
                        <TableRow><TableCell colSpan={8} sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No payments match the selected statuses.</TableCell></TableRow>
                      )}
                      {shown.map((c, i) => (
                        <TableRow key={c.payment_id || i} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary' }}>{c.payment_id || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(c.date)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(c.amount)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.method || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {c.status === 'needs_link' ? (
                              <Tooltip title="Review the transaction and order, then link them">
                                <Chip size="small" color="info" variant="outlined" icon={<LinkIcon />} label="needs link" clickable onClick={() => setLinkCharge(c)} />
                              </Tooltip>
                            ) : c.status === 'unbooked' ? (
                              <Tooltip title={data?.mapping ? 'No order yet — create one (pre-filled from the mapping) and link it' : 'No order yet — choose a devotee + product, create it and link it'}>
                                <Chip size="small" color="error" variant="outlined" icon={<AddCircleOutlineIcon />} label="create order" clickable onClick={() => setCreateCharge(c)} />
                              </Tooltip>
                            ) : (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip size="small" variant="outlined" color={statusColor(c.status)} label={statusLabel(c.status)} />
                                {c.status === 'matched' && !c.exact && (
                                  <Tooltip title={toleranceNote(c)}><WarningAmberIcon fontSize="small" color="warning" /></Tooltip>
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{(c.orders && c.orders.length) ? c.orders.map((o, k) => <Box key={k} sx={{ fontWeight: 600 }}>#{o.order_number}</Box>) : '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', verticalAlign: 'top' }}>{(c.orders && c.orders.length) ? c.orders.map((o, k) => <Box key={k}>{o.order_date ? fmtDate(o.order_date) : '—'}</Box>) : '—'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>{(c.orders && c.orders.length) ? c.orders.map((o, k) => <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}><span>{o.product_label || '—'}</span>{o.order_amount != null && <span style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtMoney(o.order_amount)}</span>}</Box>) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No payments on record.</Typography>}
              </>
            ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      <LinkChargeDialog open={!!linkCharge} charge={linkCharge} provider={provider} account={account} onClose={() => setLinkCharge(null)} onLinked={onLinked} />
      <CreateOrderDialog open={!!createCharge} charge={createCharge} provider={provider} account={account} mapping={data?.mapping} donorName={data?.donor_name} onClose={() => setCreateCharge(null)} onCreated={onCreated} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Dialog>
  );
}
